/**
 * Codex Design System — Reader Font Registry
 *
 * Defines the fonts available for users to choose in Settings →
 * Reading Preferences. The selected font is applied to both the reader
 * view and the editor.
 *
 * Each entry provides:
 *   id         — stored value in user preferences (stable identifier)
 *   label      — human-readable name shown in the font picker
 *   fontFamily — full CSS font-family stack (primary + fallbacks)
 *   category   — used to group fonts in the picker UI
 *   source     — 'google' | 'local' (informational; all loaded via index.css)
 *
 * To apply the user's chosen font:
 *   const font = getReaderFont(userPrefs.readerFontId);
 *   <div style={{ fontFamily: font.fontFamily }}>...</div>
 *   // or via CSS custom property:
 *   document.documentElement.style.setProperty('--reader-font', font.fontFamily);
 */

export type ReaderFontCategory = 'serif' | 'sans-serif' | 'accessibility';

export interface ReaderFont {
  /** Stable ID stored in user preferences */
  id: string;
  /** Display name shown in the font picker */
  label: string;
  /** Full CSS font-family stack — primary face + safe fallbacks */
  fontFamily: string;
  /** Used to group fonts in the picker UI */
  category: ReaderFontCategory;
  /** Where the font files originate (informational) */
  source: 'google' | 'local';
}

export const readerFonts = [
  // ── Serif ─────────────────────────────────────────────────────────────────
  {
    id: 'bookerly',
    label: 'Bookerly',
    fontFamily: '"Bookerly", Georgia, "Times New Roman", serif',
    category: 'serif',
    source: 'local',
  },
  {
    id: 'eb-garamond',
    label: 'EB Garamond',
    fontFamily: '"EB Garamond", Garamond, Georgia, serif',
    category: 'serif',
    source: 'google',
  },
  {
    id: 'forum',
    label: 'Forum',
    fontFamily: '"Forum", "Palatino Linotype", "Book Antiqua", Georgia, serif',
    category: 'serif',
    source: 'google',
  },
  {
    id: 'literata',
    label: 'Literata',
    fontFamily: '"Literata", Georgia, serif',
    category: 'serif',
    source: 'google',
  },
  {
    id: 'lora',
    label: 'Lora',
    fontFamily: '"Lora", Georgia, serif',
    category: 'serif',
    source: 'google',
  },
  {
    id: 'merriweather',
    label: 'Merriweather',
    fontFamily: '"Merriweather", Georgia, serif',
    category: 'serif',
    source: 'google',
  },
  {
    id: 'noto-serif',
    label: 'Noto Serif',
    fontFamily: '"Noto Serif", Georgia, serif',
    category: 'serif',
    source: 'google',
  },
  {
    id: 'publico-text',
    label: 'Publico',
    fontFamily: '"Publico Text", "Publico", Georgia, serif',
    category: 'serif',
    source: 'local',
  },
  {
    id: 'quattrocento',
    label: 'Quattrocento',
    fontFamily: '"Quattrocento", Georgia, serif',
    category: 'serif',
    source: 'google',
  },
  {
    id: 'roboto-serif',
    label: 'Roboto Serif',
    fontFamily: '"Roboto Serif", Georgia, serif',
    category: 'serif',
    source: 'google',
  },
  {
    id: 'tienne',
    label: 'Tienne',
    fontFamily: '"Tienne", Georgia, serif',
    category: 'serif',
    source: 'google',
  },
  // ── Sans-serif ────────────────────────────────────────────────────────────
  {
    id: 'gill-sans',
    label: 'Gill Sans',
    fontFamily: '"Gill Sans", "Gill Sans MT", Calibri, sans-serif',
    category: 'sans-serif',
    source: 'local',
  },
  {
    id: 'lexend',
    label: 'Lexend',
    fontFamily: '"Lexend", system-ui, Arial, sans-serif',
    category: 'sans-serif',
    source: 'google',
  },
  {
    id: 'noto-sans',
    label: 'Noto Sans',
    fontFamily: '"Noto Sans", system-ui, Arial, sans-serif',
    category: 'sans-serif',
    source: 'google',
  },
  {
    id: 'open-sans',
    label: 'Open Sans',
    fontFamily: '"Open Sans", system-ui, Arial, sans-serif',
    category: 'sans-serif',
    source: 'google',
  },
  {
    id: 'quattrocento-sans',
    label: 'Quattrocento Sans',
    fontFamily: '"Quattrocento Sans", Arial, sans-serif',
    category: 'sans-serif',
    source: 'google',
  },
  {
    id: 'roboto',
    label: 'Roboto',
    fontFamily: '"Roboto", system-ui, Arial, sans-serif',
    category: 'sans-serif',
    source: 'google',
  },
  // ── Accessibility ─────────────────────────────────────────────────────────
  {
    id: 'atkinson-hyperlegible',
    label: 'Atkinson Hyperlegible',
    fontFamily: '"Atkinson Hyperlegible", system-ui, Arial, sans-serif',
    category: 'accessibility',
    source: 'google',
  },
  {
    id: 'open-dyslexic',
    label: 'OpenDyslexic',
    fontFamily: '"OpenDyslexic", sans-serif',
    category: 'accessibility',
    source: 'local',
  },
] as const satisfies ReaderFont[];

/** Union of all valid reader font IDs */
export type ReaderFontId = (typeof readerFonts)[number]['id'];

/** Default font applied before the user makes a selection */
export const DEFAULT_READER_FONT_ID: ReaderFontId = 'bookerly';

/**
 * Look up a reader font by ID.
 * Falls back to the default if the ID is not found (e.g. stale prefs).
 */
export function getReaderFont(id: string): ReaderFont {
  return (readerFonts.find((f) => f.id === id) as ReaderFont | undefined) ?? readerFonts[0];
}

/**
 * Returns fonts grouped by category, preserving the display order
 * within each group.
 */
export function getReaderFontsByCategory(): Record<ReaderFontCategory, ReaderFont[]> {
  return readerFonts.reduce(
    (acc, font) => {
      acc[font.category].push(font as ReaderFont);
      return acc;
    },
    { serif: [], 'sans-serif': [], accessibility: [] } as Record<ReaderFontCategory, ReaderFont[]>,
  );
}
